import { AudioEngine } from './audio.ts';
import { Stage } from './stage.ts';
import { Conversation } from '@elevenlabs/client';
import { track } from './analytics.ts';
import { fxSettings } from './settings.ts';
import {
  lineCatalog, sfxCatalog, stories, VOICE_TO_MASK,
  CHARACTER_LAYOUT, PROPS,
  type Beat, type VoiceKey, type Story,
} from './script.ts';
import type { ShareCardData } from './share-card.ts';

// Module-level coordination flag — main.ts checks this before running its own
// V key handlers so the voice_prompt runner can fully own the mic lifecycle
// during a prompt (and thereby grab the recorded blob for STT).
let voicePromptActive = false;
export function isVoicePromptActive(): boolean { return voicePromptActive; }

/* No standalone STT — voice_prompt now uses a brief Convai session
 * (just the agent ID) for speech-to-text. See runVoicePrompt. */

/** Persistent state across the play — everything the player has said
 *  (voice_prompt transcripts + conversation messages) plus a key/value bag
 *  for any flags scenes want to set. Beats can branch on this, and a
 *  conversation can pass it into the agent as dynamic variables so the
 *  puppeteer actually remembers earlier moments. */
type MemoryEntry = {
  scene: string;
  source: 'user' | 'agent';
  text: string;
  ts: number;
};
/** A single choice the player committed to during the play. Captured for
 *  the end-of-play share card so the path the player took is renderable.
 *  `via` mirrors the analytics dimension — button click vs on-stage
 *  hotspot — in case we want to surface that on the card too. */
export type ChoiceRecord = {
  scene: string;
  text: string;
  goto: string;
  via: 'button' | 'hotspot';
  ts: number;
};
type Memory = {
  transcripts: MemoryEntry[];
  flags: Record<string, string | number | boolean>;
  choices: ChoiceRecord[];
  /** Wall-clock millis when play started — set by play(). Used to compute
   *  performance duration on the share card. */
  startedAt: number;
};

export class Game {
  private currentVoice: VoiceKey | null = null;
  private subtitleEl: HTMLElement;
  private choicesEl: HTMLElement;
  private story: Story = stories.original;
  private playing = false;
  private currentSceneId: string = '';
  private subtitleTypewriterTimer: number | null = null;
  /** Tracks the active backdrop key so the engine can auto-wrap a
   *  changing `set` beat with curtain close → swap → open when the
   *  curtain is currently open. Reset on setStory. */
  private currentBackdropKey: string | null = null;
  /** Whether the host sprite was visible BEFORE the puppeteer rose
   *  to take its place. Used to restore the placeholder when the
   *  puppeteer sinks back down. (With the host-skip in 'enter' beats
   *  this is almost always false, but the field is harmless.) */
  private hostWasVisible = false;
  memory: Memory = { transcripts: [], flags: {}, choices: [], startedAt: 0 };
  /** The ending the player most recently reached. Set inside showEnding;
   *  read by main.ts when assembling share-card data. */
  private lastEnding: { key: string; label: string } | null = null;
  /** Fires the moment a voice line actually starts playing (i.e. after any
   *  mask-swap breath/clack). main.ts uses this to retarget the spotlight. */
  onLineStart: ((voice: VoiceKey) => void) | null = null;

  constructor(
    private stage: Stage,
    private audio: AudioEngine,
  ) {
    this.subtitleEl = document.getElementById('subtitle')!;
    this.choicesEl = document.getElementById('choices')!;
  }

  /** Pick which story this Game will play. Call before play().
   *  Also resets memory so a replay starts with a clean transcript and
   *  empty choice path — without this the share card from a second
   *  playthrough would contain choices from the first. */
  setStory(story: Story) {
    this.story = story;
    this.currentBackdropKey = null;
    this.memory = { transcripts: [], flags: {}, choices: [], startedAt: 0 };
    this.currentVoice = null;
  }

  /** Record a single utterance into memory. Used by both voice_prompt (after
   *  STT) and conversation (via onMessage callback). */
  recordTranscript(source: 'user' | 'agent', text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    this.memory.transcripts.push({
      scene: this.currentSceneId,
      source,
      text: trimmed,
      ts: Date.now(),
    });
    console.log(`[memory] ${this.currentSceneId} ${source}: ${trimmed.slice(0, 100)}`);
  }

  /** True if the player has produced any non-empty user transcript so far. */
  hasUserSpoken(): boolean {
    return this.memory.transcripts.some(t => t.source === 'user');
  }

  /** Build the data payload for the end-of-play share card. Returns null
   *  if no ending has been reached yet (e.g. someone clicked the share
   *  button before showEnding ran — defensive). */
  getShareData(): ShareCardData | null {
    if (!this.lastEnding) return null;
    const startedAt = this.memory.startedAt || 0;
    const durationMs = startedAt > 0 ? Date.now() - startedAt : null;
    return {
      endingKey: this.lastEnding.key,
      endingLabel: this.lastEnding.label,
      choices: this.memory.choices.map(c => ({ text: c.text })),
      recallSnippet: pickRecallSnippet(this.memory.transcripts),
      durationMs,
      storyTitle: this.story.title,
    };
  }

  /** Render memory as a readable transcript suitable for injecting into a
   *  Convai agent's prompt via dynamic variables. */
  getMemorySummary(): string {
    if (this.memory.transcripts.length === 0) return '(no prior conversation)';
    return this.memory.transcripts
      .map(t => `${t.source === 'user' ? 'Player' : 'Puppeteer'}: ${t.text}`)
      .join('\n');
  }

  /** Preload every line and SFX before the play starts. */
  async preload() {
    const voiceLoads = Object.values(lineCatalog).map(line => ({
      key: line.key,
      url: `/audio/voices/${line.key}.mp3`,
    }));
    const sfxLoads = Object.keys(sfxCatalog).map(key => ({
      key,
      url: `/audio/sfx/${key}.mp3`,
    }));
    await this.audio.loadMany([...voiceLoads, ...sfxLoads]);
  }

  /** Start the play. Defaults to the configured story's first scene.
   *  Guards against double-start — if a play is already running (e.g. user
   *  rapid-clicks the story picker) the second call is a no-op. */
  async play(sceneId: string = this.story.firstScene): Promise<void> {
    if (this.playing) {
      console.warn('[game] play() called while already playing — ignored');
      return;
    }
    this.playing = true;
    this.memory.startedAt = Date.now();
    track('story_started', { storyId: this.story.id, firstScene: sceneId });
    try {
      let nextScene: string | undefined = sceneId;
      while (nextScene) {
        const scene = this.story.scenes[nextScene];
        if (!scene) {
          console.error(`[game] unknown scene in story "${this.story.id}": ${nextScene}`);
          break;
        }
        this.currentSceneId = nextScene;
        track('scene_entered', { storyId: this.story.id, scene: nextScene });
        nextScene = undefined;
        for (const beat of scene.beats) {
          const next = await this.runBeat(beat);
          if (next) { nextScene = next; break; }
        }
      }
    } finally {
      this.playing = false;
    }
  }

  /** @returns next scene id if the beat redirects, otherwise undefined. */
  private async runBeat(beat: Beat): Promise<string | undefined> {
    switch (beat.type) {
      case 'line':
        await this.runLine(beat.key);
        return;
      case 'sfx':
        await this.audio.playSfx(beat.key, { volume: beat.volume });
        return;
      case 'wait':
        await wait(beat.ms);
        return;
      case 'view':
        this.stage.setView(beat.view, beat.duration ?? 1200);
        this.audio.setRoomTone(beat.view === 'audience' ? 1.0 : 0.4);
        await wait(beat.duration ?? 1200);
        return;
      case 'ambient':
        if (beat.key === null) this.audio.stopAmbient();
        else this.audio.startAmbient(beat.key, beat.volume ?? 1);
        return;
      case 'puppet':
        this.stage.setPuppet(beat.visible, beat.color);
        return;
      case 'choice':
        return await this.runChoice(beat.options);
      case 'goto':
        return beat.scene;
      case 'end':
        void this.showEnding(beat.ending);
        return;
      case 'enter': {
        // Host is a pure narrator — never show the greyman placeholder
        // for them sliding in. When a host line plays, the puppeteer
        // rises from below the stage instead (see runLine).
        if (beat.who === 'host') return;
        // If a side is explicitly set, honor it. Otherwise: when the
        // curtain is OPEN we slide the sprite in from the closer edge
        // (so a character never just pops into the middle of a visible
        // stage); when the curtain is CLOSED we snap to home so the
        // sprite is already in place when the curtain rises.
        let side: 'left' | 'right' | 'center' = beat.side ?? 'center';
        if (side === 'center' && this.stage.isCurtainOpen()) {
          const homeX = CHARACTER_LAYOUT[beat.who]?.homeX ?? 0;
          side = homeX < 0 ? 'left' : 'right';
        }
        this.stage.sprite(beat.who)?.enter(side);
        return;
      }
      case 'exit':
        if (beat.who === 'host') return;
        this.stage.sprite(beat.who)?.exit(beat.side ?? 'left');
        return;
      case 'move':
        this.stage.sprite(beat.who)?.moveTo(beat.x, beat.y);
        return;
      case 'lean':
        this.stage.sprite(beat.who)?.lean(beat.deg);
        return;
      case 'shake':
        this.stage.sprite(beat.who)?.shake();
        return;
      case 'place': {
        // Modular scene-setup beat. Closes the curtain (if open), clears
        // the stage, applies backdrop + music, snap-spawns the listed
        // props and characters at home WITHOUT slide animation, then
        // re-opens the curtain. Subsequent enter/prop beats during the
        // scene still use slide animations as usual.
        if (this.stage.isCurtainOpen()) await this.closeCurtainAnim();
        this.stage.clearStage();
        this.stage.setBackdrop(beat.set);
        this.currentBackdropKey = beat.set;
        if (beat.music === null) {
          this.audio.stopMusic();
        } else if (beat.music !== undefined) {
          this.audio.startMusic(beat.music, 0.35);
        }
        for (const key of beat.props ?? []) {
          this.stage.prop(key)?.snapVisible();
        }
        for (const c of beat.characters ?? []) {
          if (typeof c === 'string') {
            this.stage.sprite(c)?.snapVisible();
          } else {
            this.stage.sprite(c.who)?.snapVisible(c.x, c.y);
          }
        }
        await wait(150);            // brief settle so the swap renders
        await this.openCurtainAnim();
        return;
      }
      case 'set': {
        // Auto-wrap a place change with curtain close → swap → open if
        // the curtain is currently OPEN and the backdrop is actually
        // changing. If the script already explicitly closed the curtain
        // (the established "scene change" pattern) we just swap — the
        // script's later `curtain open` will reveal the new place.
        const changingPlace =
          this.currentBackdropKey !== null && this.currentBackdropKey !== beat.key;
        if (changingPlace && this.stage.isCurtainOpen()) {
          await this.closeCurtainAnim();
          this.stage.setBackdrop(beat.key);
          await wait(250);
          await this.openCurtainAnim();
        } else {
          this.stage.setBackdrop(beat.key);
        }
        this.currentBackdropKey = beat.key;
        return;
      }
      case 'curtain':
        if (beat.action === 'close') await this.closeCurtainAnim();
        else                          await this.openCurtainAnim();
        return;
      case 'music':
        if (beat.key === null) this.audio.stopMusic();
        else this.audio.startMusic(beat.key, beat.volume ?? 0.4);
        return;
      case 'prop': {
        if (beat.action === 'show') {
          // Same rule as character enter — slide props in from the
          // closer side when the curtain is open, snap when closed.
          let side: 'left' | 'right' | 'center' = 'center';
          if (this.stage.isCurtainOpen()) {
            const x = PROPS[beat.key]?.x ?? 0;
            side = x < 0 ? 'left' : 'right';
          }
          this.stage.prop(beat.key)?.enter(side);
        } else {
          this.stage.prop(beat.key)?.exit('left');
        }
        return;
      }
      case 'voice_prompt':
        return await this.runVoicePrompt(beat);
      case 'conversation':
        return await this.runConversation(beat);
      case 'branch_if_spoke': {
        const goto = this.hasUserSpoken() ? beat.whenSpoken : beat.whenSilent;
        console.log(`[branch_if_spoke] hasUserSpoken=${this.hasUserSpoken()} → ${goto}`);
        return goto;
      }
      case 'set_flag': {
        const v = beat.value ?? true;
        this.memory.flags[beat.key] = v;
        console.log(`[set_flag] ${beat.key}=${JSON.stringify(v)}`);
        return;
      }
      case 'branch_if_flag': {
        const cur = this.memory.flags[beat.key];
        const matches = beat.equals !== undefined ? cur === beat.equals : !!cur;
        const goto = matches ? beat.whenMatch : beat.whenNotMatch;
        console.log(`[branch_if_flag] ${beat.key}=${JSON.stringify(cur)} → ${goto}`);
        return goto;
      }
      case 'branch_if_count': {
        const count = beat.flags.filter(k => !!this.memory.flags[k]).length;
        const goto = count >= beat.atLeast ? beat.whenAtLeast : beat.whenBelow;
        console.log(`[branch_if_count] ${count}/${beat.flags.length} → ${goto}`);
        return goto;
      }
    }
  }

  /** Runs a free-form back-and-forth with the ElevenLabs Conversational AI
   *  agent. Player talks naturally; agent listens + replies via TTS. Ends
   *  when the user clicks the on-screen "continue" button (or the agent
   *  decides via its own logic) — at which point we resolve to onExit. */
  private async runConversation(beat: Extract<Beat, { type: 'conversation' }>): Promise<string> {
    const overlay = document.getElementById('conversation-overlay');
    const statusEl = document.getElementById('conv-status');
    const continueBtn = document.getElementById('conv-continue') as HTMLButtonElement | null;
    const agentId = (import.meta as any).env?.VITE_ELEVENLABS_AGENT_ID as string | undefined;

    const setStatus = (s: string) => { if (statusEl) statusEl.textContent = s; };
    const showOverlay = (yes: boolean) => {
      if (!overlay) return;
      if (yes) {
        overlay.classList.remove('hidden');
        void overlay.offsetWidth;
        overlay.classList.add('active');
      } else {
        overlay.classList.remove('active');
        setTimeout(() => overlay.classList.add('hidden'), 280);
      }
    };

    if (!agentId) {
      console.warn('[conversation] VITE_ELEVENLABS_AGENT_ID missing; skipping conversation beat');
      return beat.onExit;
    }

    showOverlay(true);
    setStatus('connecting…');
    this.stage.setPuppeteerVisible(true);   // show puppeteer figure during the chat
    // Note: we do NOT set voicePromptActive here. main.ts's V handler
    // should keep running so it opens its own mic stream + analyser
    // for the volume meter. The ElevenLabs SDK opens a SEPARATE mic
    // stream of its own (the two are independent). voicePromptActive
    // is reserved for the voice_prompt beat, where game.ts grabs the
    // recorded blob for STT and main.ts must stay out of the way.
    const conversationScene = this.currentSceneId;
    const transcriptCountAtStart = this.memory.transcripts.length;
    track('conversation_started', {
      storyId: this.story.id,
      scene: conversationScene,
      passMemory: !!beat.passMemory,
    });

    // Lower music while the conversation is happening so the agent's voice
    // sits cleanly. Restored on exit.
    const previousMusicDuck = 0.12;
    this.audio.setMusicDuck(previousMusicDuck);

    return new Promise<string>((resolve) => {
      let session: Awaited<ReturnType<typeof Conversation.startSession>> | null = null;
      let resolved = false;
      let vHeld = false;
      let agentSpeaking = false;

      const updateStatus = () => {
        if (!session) return;
        if (agentSpeaking) setStatus('the puppeteer is speaking…');
        else if (vHeld)    setStatus('🎙 listening to you — release V to stop');
        else               setStatus('hold V to speak');
      };

      const finish = (reason: string) => {
        if (resolved) return;
        resolved = true;
        console.log(`[conversation] resolve: reason=${reason}`);
        track('conversation_ended', {
          storyId: this.story.id,
          scene: conversationScene,
          reason,
          newTranscripts: this.memory.transcripts.length - transcriptCountAtStart,
        });
        this.audio.setMusicDuck(1.0);
        this.hideSubtitle();
        this.stage.setPuppeteerVisible(false);
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('keyup', onKeyUp);
        if (continueBtn) continueBtn.onclick = null;
        showOverlay(false);
        if (session) {
          try { void session.endSession(); } catch { /* already closed */ }
          session = null;
        }
        resolve(beat.onExit);
      };

      // V-gated mic: the SDK keeps the connection alive but its mic input is
      // muted unless V is held. The agent literally hears nothing while V is
      // up — no eavesdropping, no accidental mid-thought interruptions.
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.code !== 'KeyV' || vHeld || e.repeat) return;
        vHeld = true;
        try { session?.setMicMuted(false); } catch { /* not yet connected */ }
        updateStatus();
      };
      const onKeyUp = (e: KeyboardEvent) => {
        if (e.code !== 'KeyV' || !vHeld) return;
        vHeld = false;
        try { session?.setMicMuted(true); } catch { /* not yet connected */ }
        updateStatus();
      };
      window.addEventListener('keydown', onKeyDown);
      window.addEventListener('keyup', onKeyUp);

      // Continue button — user signals "I'm done, run the play".
      if (continueBtn) {
        continueBtn.onclick = async () => {
          setStatus('finishing…');
          try { await session?.endSession(); } catch (err) { console.warn('[conversation] endSession err', err); }
          setTimeout(() => finish('button-fallback'), 1500);
        };
      }

      Conversation.startSession({
        agentId,
        // Force WebSocket — the WebRTC/LiveKit transport currently 404s on
        // /rtc/v1/validate. WebSocket is the older/more reliable path.
        connectionType: 'websocket',
        overrides: {
          agent: {
            prompt: { prompt: beat.systemPrompt },
            ...(beat.firstMessage ? { firstMessage: beat.firstMessage } : {}),
          },
        },
        // When the beat asks for it, inject the cumulative memory as a
        // dynamic variable {{prior_chat}} that the system prompt can
        // reference. The agent then "remembers" earlier acts.
        ...(beat.passMemory ? {
          dynamicVariables: { prior_chat: this.getMemorySummary() },
        } : {}),
        onConnect: () => {
          console.log('[conversation] connected');
          try { session?.setMicMuted(true); } catch { /* */ }
          updateStatus();
        },
        onDisconnect: () => {
          console.log('[conversation] disconnected');
          finish('disconnect');
        },
        onError: (msg, ctx) => {
          console.warn('[conversation] error', msg, ctx);
          setStatus(`error: ${msg}`);
          setTimeout(() => finish('error'), 1500);
        },
        onModeChange: ({ mode }) => {
          agentSpeaking = (mode === 'speaking');
          updateStatus();
        },
        onMessage: ({ source, message }) => {
          // Persist both sides of the conversation into memory so later
          // scenes (and later conversations) can reference what was said.
          if (typeof message !== 'string' || !message.trim()) return;
          this.recordTranscript(source === 'user' ? 'user' : 'agent', message);
          // Render the agent's spoken response as a subtitle while the
          // SDK streams its TTS. When the player speaks next (or the
          // conversation ends), we hide it. No typewriter — we don't
          // have the TTS duration, just paint the full text instantly.
          if (source !== 'user') {
            this.showSubtitle(message);
          } else {
            this.hideSubtitle();
          }
        },
      }).then((s) => {
        session = s;
        // Apply the initial mute as soon as the session object exists, in
        // case onConnect hasn't fired yet.
        try { s.setMicMuted(true); } catch { /* */ }
      }).catch((err) => {
        console.warn('[conversation] startSession failed', err);
        setStatus(`connection failed: ${err?.message ?? err}`);
        setTimeout(() => finish('start-failed'), 1500);
      });
    });
  }

  /** Voice prompt runner. Flow:
   *   1. Show "press V and speak" hint and set the global voicePromptActive
   *      flag so main.ts's V handlers skip their own openMic/closeMic.
   *   2. Wait for V keydown → audio.openMic() (starts MediaRecorder).
   *   3. While V is held, narrator stays silent. User speaks freely.
   *   4. On V keyup → audio.closeMic() returns the recorded Blob.
   *      POST that blob to ElevenLabs Speech-to-Text. Resolve to onSpeak if
   *      the transcript is non-empty, otherwise onSilence.
   *   5. If V is never pressed before `timeoutMs`, resolve to onSilence.
   */
  private lastTranscript: string = '';
  /** Read the most recent voice_prompt transcript. Scenes can branch on
   *  what the player actually said by inspecting this. */
  getLastTranscript(): string { return this.lastTranscript; }

  private runVoicePrompt(beat: Extract<Beat, { type: 'voice_prompt' }>): Promise<string> {
    const hintEl = document.getElementById('voice-prompt-hint');
    const timeoutMs = beat.timeoutMs ?? 12000;
    const agentId = (import.meta as any).env?.VITE_ELEVENLABS_AGENT_ID as string | undefined;

    if (hintEl) {
      hintEl.classList.remove('hidden');
      void hintEl.offsetWidth;
      hintEl.classList.add('active');
    }
    this.stage.setPuppeteerVisible(true);   // visible while waiting for player's voice
    console.log(`[voice_prompt] start: pressV-timeout=${timeoutMs}ms`);

    if (!agentId) {
      console.warn('[voice_prompt] VITE_ELEVENLABS_AGENT_ID missing; falling through to onSilence');
      return new Promise<string>(r => setTimeout(() => r(beat.onSilence), 200));
    }

    return new Promise<string>(resolve => {
      let session: Awaited<ReturnType<typeof Conversation.startSession>> | null = null;
      let resolved = false;
      let vHeld = false;
      let everPressed = false;
      let processing = false;
      let userTranscript = '';
      const startedAt = performance.now();

      const close = (goto: string, reason: string, transcript: string = '') => {
        if (resolved) return;
        resolved = true;
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('keyup', onKeyUp);
        this.stage.setPuppeteerVisible(false);
        this.lastTranscript = transcript;
        if (transcript) this.recordTranscript('user', transcript);
        const elapsed = performance.now() - startedAt;
        console.log(`[voice_prompt] resolve: goto=${goto} reason=${reason} ` +
          `transcript=${JSON.stringify(transcript)} ` +
          `elapsed=${elapsed.toFixed(0)}ms`);
        track('voice_prompt_resolved', {
          storyId: this.story.id,
          scene: this.currentSceneId,
          goto,
          reason,
          spoke: transcript.length > 0,
          transcriptLen: transcript.length,
          elapsedMs: Math.round(elapsed),
        });
        if (hintEl) {
          hintEl.classList.remove('active');
          setTimeout(() => hintEl.classList.add('hidden'), 280);
        }
        if (session) {
          try { void session.endSession(); } catch { /* already closed */ }
          session = null;
        }
        resolve(goto);
      };

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.code !== 'KeyV' || vHeld || e.repeat || processing) return;
        vHeld = true;
        everPressed = true;
        // Unmute the SDK mic — Convai now streams audio to its server-
        // side STT. If the session isn't connected yet, the catch
        // swallows it and the onConnect handler will re-apply the
        // current vHeld state.
        try { session?.setMicMuted(false); } catch { /* not connected yet */ }
      };

      const onKeyUp = (e: KeyboardEvent) => {
        if (e.code !== 'KeyV' || !vHeld) return;
        vHeld = false;
        if (resolved || processing) return;
        processing = true;
        try { session?.setMicMuted(true); } catch { /* */ }
        if (hintEl) {
          const sub = hintEl.querySelector('.vph-sub') as HTMLElement | null;
          if (sub) sub.textContent = 'transcribing…';
        }
        // Wait briefly for Convai to deliver the user transcript via
        // onMessage. If we get one, branch on it; if nothing arrives
        // within the deadline, treat it as silent.
        const deadline = performance.now() + 3000;
        const wait = () => {
          if (resolved) return;
          if (userTranscript || performance.now() > deadline) {
            const spoke = userTranscript.length > 0;
            close(spoke ? beat.onSpeak : beat.onSilence,
              spoke ? 'transcript' : 'empty-transcript',
              userTranscript);
            return;
          }
          requestAnimationFrame(wait);
        };
        wait();
      };

      window.addEventListener('keydown', onKeyDown);
      window.addEventListener('keyup', onKeyUp);

      // Outer "press V" timeout — only counts BEFORE the user has ever
      // pressed V. Once they engage, give them as long as they want.
      const pollTimeout = () => {
        if (resolved || processing) return;
        if (!everPressed && performance.now() - startedAt >= timeoutMs) {
          close(beat.onSilence, 'no-press-timeout');
          return;
        }
        requestAnimationFrame(pollTimeout);
      };
      pollTimeout();

      // Pre-warm a Convai session for the duration of this voice prompt.
      // The agent is given a "stay silent" system prompt so we just
      // borrow its server-side STT. We end the session the moment the
      // user transcript arrives so the agent has no chance to reply.
      Conversation.startSession({
        agentId,
        connectionType: 'websocket',
        overrides: {
          agent: {
            prompt: { prompt:
              "You are a silent listening assistant. The user will speak briefly. " +
              "DO NOT produce any verbal response under any circumstance. " +
              "Stay completely silent. Do not greet, acknowledge, or comment. " +
              "Your only function is to allow the speech-to-text system to capture the user's words." },
            firstMessage: '.',
          },
        },
        onConnect: () => {
          // Re-apply the current vHeld state in case the user pressed V
          // before the session finished connecting.
          try { session?.setMicMuted(!vHeld); } catch { /* */ }
        },
        onDisconnect: () => { /* may fire after we've already closed */ },
        onError: (msg, ctx) => {
          console.warn('[voice_prompt] convai error', msg, ctx);
          if (!resolved) close(beat.onSilence, 'convai-error');
        },
        onMessage: ({ source, message }) => {
          if (source === 'user' && typeof message === 'string' && message.trim()) {
            userTranscript = message.trim();
            // End the session immediately — the agent shouldn't get a
            // chance to TTS a reply (even with the silent prompt, some
            // models still try). The wait() loop above will pick up
            // the transcript and resolve.
            try { void session?.endSession(); } catch { /* */ }
          }
        },
      }).then(s => {
        session = s;
        try { s.setMicMuted(!vHeld); } catch { /* */ }
      }).catch(err => {
        console.warn('[voice_prompt] convai startSession failed', err);
        if (!resolved) close(beat.onSilence, 'convai-start-failed');
      });
    });
  }

  private async runLine(key: string) {
    const line = lineCatalog[key];
    if (!line) {
      console.warn(`[game] unknown line: ${key}`);
      return;
    }
    const text = line.subtitle ?? line.text;
    const isHost = line.voice === 'host';
    const speaker = this.stage.sprite(line.voice);

    if (isHost) {
      // Host narration → puppeteer rises from beneath the stage and
      // STAYS up until either a non-host line plays or the curtain
      // closes (scene transition). No idle timer — consecutive host
      // lines, host lines with `wait` beats between, etc. all keep
      // the puppeteer on stage.
      const hostSprite = this.stage.sprite('host');
      if (hostSprite && hostSprite.isVisible()) {
        this.hostWasVisible = true;
        hostSprite.hide();
      }
      this.stage.setPuppeteerVisible(true);
    } else {
      // Non-host line: slide the puppeteer down NOW (if up) so the
      // new speaker has the stage to themselves.
      this.lowerPuppeteerAndRestoreHost();
      speaker?.setTalking(true);
    }

    // Fires the instant the voice actually starts (after breath/clack on a
    // mask swap). Drives both the typewriter and the spotlight handoff.
    const onLineStart = () => {
      const durationSec = this.audio.getLineDuration(line.key) ?? estimateDuration(text);
      this.showSubtitle(text, durationSec * 1000);
      this.onLineStart?.(line.voice);
    };

    if (line.voice !== this.currentVoice) {
      const maskIndex = VOICE_TO_MASK[line.voice];
      this.currentVoice = line.voice;
      await this.audio.swapMask({
        nextLine: line.key,
        onMaskLifted: () => {
          if (maskIndex !== null) this.stage.liftMask(maskIndex);
        },
        onLineStart,
      });
    } else {
      onLineStart();
      await this.audio.playLine(line.key);
    }

    if (!isHost) {
      speaker?.setTalking(false);
    }
    // Host: leave the puppeteer up. He'll be lowered the next time a
    // non-host line plays OR the curtain closes (see openCurtainAnim
    // / closeCurtainAnim and the curtain / place / set beat handlers).
    this.hideSubtitle();
  }

  /** Always-pair curtain SFX with the curtain animation. Called from
   *  every code path that triggers a curtain (curtain beat, place,
   *  auto-wrap on `set`). Closing also lowers the puppeteer so he
   *  doesn't peek above a closed curtain. */
  private async openCurtainAnim() {
    void this.audio.playSfx('curtain_open', { volume: 0.7 });
    await this.stage.curtainOpen();
  }
  private async closeCurtainAnim() {
    this.lowerPuppeteerAndRestoreHost();
    void this.audio.playSfx('curtain_close', { volume: 0.7 });
    await this.stage.curtainClose();
  }

  /** Slide the puppeteer mesh back below the stage and restore the
   *  greyman host placeholder if it was visible. */
  private lowerPuppeteerAndRestoreHost() {
    this.stage.setPuppeteerVisible(false);
    if (this.hostWasVisible) {
      this.stage.sprite('host')?.show();
      this.hostWasVisible = false;
    }
  }

  private runChoice(options: Array<{ text: string; goto: string; target?: string }>): Promise<string> {
    return new Promise(resolve => {
      this.choicesEl.innerHTML = '';
      const sceneAtChoice = this.currentSceneId;

      const finish = (goto: string, text: string, via: 'button' | 'hotspot') => {
        this.memory.choices.push({
          scene: sceneAtChoice, text, goto, via, ts: Date.now(),
        });
        track('choice_picked', {
          storyId: this.story.id,
          scene: sceneAtChoice,
          choice: text,
          goto,
          via,
        });
        this.choicesEl.classList.remove('visible');
        this.choicesEl.classList.add('hidden');
        this.choicesEl.innerHTML = '';
        this.stage.clearHotspots();
        resolve(goto);
      };

      options.forEach(opt => {
        const btn = document.createElement('button');
        btn.className = 'choice';
        btn.textContent = opt.text;
        btn.onclick = () => finish(opt.goto, opt.text, 'button');
        this.choicesEl.appendChild(btn);

        // Also accept a click on the target sprite/prop on the stage —
        // diegetic hotspot input alongside the text button.
        if (opt.target) {
          this.stage.setupHotspot(opt.target, () => finish(opt.goto, opt.text, 'hotspot'));
        }
      });
      this.choicesEl.classList.remove('hidden');
      // Force reflow to enable transition.
      void this.choicesEl.offsetWidth;
      this.choicesEl.classList.add('visible');
    });
  }

  private showSubtitle(text: string, durationMs?: number) {
    this.cancelTypewriter();
    this.subtitleEl.classList.remove('hidden');
    void this.subtitleEl.offsetWidth;
    this.subtitleEl.classList.add('visible');

    // Plain mode (or no duration) — snap full text in.
    if (!fxSettings.typewriter || !durationMs || durationMs <= 0) {
      this.subtitleEl.textContent = text;
      return;
    }

    // Typewriter mode — reveal char-by-char so the last char lands as the
    // line ends. Reveal completes at ~92% of the audio so the eye finishes
    // reading just before the voice does.
    this.subtitleEl.textContent = '';
    const total = text.length;
    if (total === 0) return;
    const revealMs = Math.max(400, durationMs * 0.92);
    const perChar = revealMs / total;
    let i = 0;
    const tick = () => {
      i++;
      this.subtitleEl.textContent = text.slice(0, i);
      if (i < total) {
        this.subtitleTypewriterTimer = window.setTimeout(tick, perChar);
      } else {
        this.subtitleTypewriterTimer = null;
      }
    };
    this.subtitleTypewriterTimer = window.setTimeout(tick, perChar);
  }

  private cancelTypewriter() {
    if (this.subtitleTypewriterTimer !== null) {
      clearTimeout(this.subtitleTypewriterTimer);
      this.subtitleTypewriterTimer = null;
    }
  }

  private hideSubtitle() {
    this.cancelTypewriter();
    this.subtitleEl.classList.remove('visible');
  }

  async showEnding(ending: string) {
    // Hide any leftover line subtitle.
    this.subtitleEl.classList.remove('visible');

    track('ending_reached', {
      storyId: this.story.id,
      ending,
      transcriptCount: this.memory.transcripts.length,
      flagCount: Object.keys(this.memory.flags).length,
    });

    const screen = document.getElementById('end-screen');
    if (!screen) return;
    const labels: Record<string, string> = {
      gentle:     'a gentle ending',
      bitter:     'a bitter ending',
      remembered: 'remembered',
      silenced:   'left in silence',
      forgiven:   'forgiven',
      left:       'they always do',
      carried_home: 'carried home',
      traded_self:  'traded self for them',
      alone:        'returned alone',
      haunted:      'something else came back',
      // Triggered when the player runs out of lives (heart bar empties).
      failure:    'the heart gave out',
    };
    const endingLabel = labels[ending] ?? ending;
    // Stash for the share-card builder. main.ts reads this when the
    // share buttons are clicked.
    this.lastEnding = { key: ending, label: endingLabel };

    const subtitle = screen.querySelector('.end-subtitle') as HTMLElement | null;
    if (subtitle) subtitle.textContent = endingLabel;

    // Mirror the ending label into the credit-roll header so the
    // fin + ending type also scroll up at the start of the credits,
    // not just sit statically on the end card. The fin line itself
    // is hardcoded in the HTML; only the ending text is dynamic.
    const creditEnding = screen.querySelector('.end-credit-ending') as HTMLElement | null;
    if (creditEnding) creditEnding.textContent = endingLabel;

    const recall = screen.querySelector('.end-recall') as HTMLElement | null;
    if (recall) {
      const snippet = pickRecallSnippet(this.memory.transcripts);
      if (snippet) {
        recall.textContent = `“${snippet}”`;
        recall.classList.remove('hidden');
      } else {
        recall.classList.add('hidden');
      }
    }

    screen.classList.remove('hidden');
    // Force reflow so the opacity transition triggers.
    void (screen as HTMLElement).offsetWidth;
    screen.classList.add('fading');

    await wait(1500);                 // fade-to-black completes
    screen.classList.add('show-card');
    await wait(3000);                 // hold the "fin" card
    screen.classList.add('show-credits');
    await wait(20500);                // ~22s credit roll, leave 1.5s for button
    screen.classList.add('show-replay');
  }
}

function wait(ms: number) {
  return new Promise<void>(r => setTimeout(r, ms));
}

/** Fallback when an audio buffer's duration isn't available — assume ~14
 *  characters per second (a slow theatrical cadence). */
function estimateDuration(text: string): number {
  return Math.max(1.0, text.length / 14);
}

/** Pick the most evocative user transcript for the end-card recall.
 *  Prefers the longest non-trivial user line, trimmed to ~80 chars at a
 *  word boundary. Returns null if there's nothing worth quoting. */
function pickRecallSnippet(
  transcripts: ReadonlyArray<{ source: 'user' | 'agent'; text: string }>,
): string | null {
  const userLines = transcripts
    .filter(t => t.source === 'user' && t.text.trim().length >= 4);
  if (userLines.length === 0) return null;

  const best = userLines.reduce((a, b) =>
    b.text.trim().length > a.text.trim().length ? b : a);
  const text = best.text.trim().replace(/\s+/g, ' ');
  const MAX = 90;
  if (text.length <= MAX) return text;

  const cut = text.slice(0, MAX);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut) + '…';
}
